import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Catalog from "@/pages/Catalog";
import AIAssistant from "@/pages/AIAssistant";
import Approvals from "@/pages/Approvals";
import Manager from "@/pages/Manager";
import AgentSettings from "@/pages/AgentSettings";
import AgentChat from "@/pages/AgentChat";
import Settings from "@/pages/Settings";
import Quotations from "@/pages/Quotations";
import QuotationDetail from "@/pages/QuotationDetail";
import Invoices from "@/pages/Invoices";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Portal from "@/pages/Portal";
import { ForgotPassword, ResetPassword, ChangePasswordForm } from "@/pages/AuthPages";
import { PageHeader } from "@/components/Common";
import Notifications from "@/pages/Notifications";
import PriceHistory from "@/pages/PriceHistory";
import Users from "@/pages/Users";
import Partners from "@/pages/Partners";
import PublicRequest from "@/pages/PublicRequest";
import Embed from "@/pages/Embed";
import Landing from "@/pages/Landing";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground font-mono text-sm">
        Memuat…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "customer") return <Navigate to="/portal" replace />;
  return <Layout>{children}</Layout>;
}

function CustomerOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "customer") return <Navigate to="/dashboard" replace />;
  return children;
}

const Account = () => (
  <div data-testid="account-page"><PageHeader overline="Akun" title="Ganti Password" /><div className="p-8"><ChangePasswordForm /></div></div>
);

function AdminOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground font-mono text-sm">
        Memuat…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/portal" element={<CustomerOnly><Portal /></CustomerOnly>} />
            <Route path="/account" element={<Protected><Account /></Protected>} />
            <Route path="/beranda" element={<Landing />} />
            <Route path="/request" element={<PublicRequest />} />
            <Route path="/embed" element={<Embed />} />
            <Route path="/" element={<Landing />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/catalog" element={<Protected><Catalog /></Protected>} />
            <Route path="/price-history" element={<AdminOnly><PriceHistory /></AdminOnly>} />
            <Route path="/users" element={<AdminOnly><Users /></AdminOnly>} />
            <Route path="/settings" element={<AdminOnly><Settings /></AdminOnly>} />
            <Route path="/partners" element={<Navigate to="/customers" replace />} />
            <Route path="/customers" element={<Protected><Partners kind="customers" /></Protected>} />
            <Route path="/suppliers" element={<Protected><Partners kind="suppliers" /></Protected>} />
            <Route path="/ai" element={<Protected><AIAssistant /></Protected>} />
            <Route path="/agents" element={<Protected><AgentChat /></Protected>} />
            <Route path="/approvals" element={<AdminOnly><Approvals /></AdminOnly>} />
            <Route path="/manager" element={<Protected><Manager /></Protected>} />
            <Route path="/agent-settings" element={<AdminOnly><AgentSettings /></AdminOnly>} />
            <Route path="/quotations" element={<Protected><Quotations /></Protected>} />
            <Route path="/quotations/:id" element={<Protected><QuotationDetail /></Protected>} />
            <Route path="/invoices" element={<AdminOnly><Invoices /></AdminOnly>} />
            <Route path="/purchase-orders" element={<AdminOnly><PurchaseOrders /></AdminOnly>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="light" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
